// Mapping utilities: backend DTO -> domain interfaces (and minimal reverse)
// These helpers are defensive: they accept unknown shapes and normalize casing.

import {
Bop,
BopModel,
BopModelTranslation,
BopOperation,


Coefficient,

CoefficientConfig,CoefficientOption,

CoefficientSelectedValue,
// Feb/SubBop domain interfaces are assumed available in models
Operation
} from "./models";

type AnyRecord = Record<string, any>;

function val(o: AnyRecord, ...candidates: string[]) {
  for (const k of candidates) {
    if (o && Object.prototype.hasOwnProperty.call(o, k)) return o[k];
  }
  return undefined;
}

function toStringOrUndefined(v: any): string | undefined {
  return v == null ? undefined : String(v);
}

function normalizeDate(v: any): string | undefined {
  if (!v) return undefined;
  try {
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d.toISOString();
  } catch {
    return undefined;
  }
}

// ---------------- BopModel ----------------
export function mapBopModelDto(dto: AnyRecord): BopModel {
  if (!dto) return { key: "" } as BopModel; // fallback
  const translations: BopModelTranslation[] | undefined = Array.isArray(
    dto.Translations
  )
    ? dto.Translations.map((t: AnyRecord) => ({
        languageCode: val(t, "LanguageCode", "languageCode", "language") ?? "",
        name: val(t, "Name", "name") ?? "",
        description: val(t, "Description", "description"),
      }))
    : undefined;
  return {
    id: toStringOrUndefined(val(dto, "Id", "id")),
    key: val(dto, "Key", "key") ?? "",
    name: val(dto, "Name", "name"),
    description: val(dto, "Description", "description"),
    translations,
    supportsType5Coefficients: val(
      dto,
      "SupportsType5Coefficients",
      "supportsType5Coefficients"
    ),
    requiresVisaValidation: val(
      dto,
      "RequiresVisaValidation",
      "requiresVisaValidation"
    ),
    fieldConfigFile: val(dto, "FieldConfigFile", "fieldConfigFile"),
    rawConfig: val(dto, "RawConfig", "rawConfig"),
  };
}

// ---------------- CoefficientOption ----------------
export function mapCoefficientOptionDto(dto: AnyRecord): CoefficientOption {
  return {
    id: toStringOrUndefined(val(dto, "Id", "id")) || "",
    key: val(dto, "Key", "key"),
    name: val(dto, "Name", "name"),
    label: val(dto, "Label", "label", "Name", "name"),
    value: val(dto, "Value", "value", "FirstValue"),
    secondValue: val(dto, "SecondValue", "secondValue"),
    flagAction: val(dto, "FlagAction", "flagAction"),
    unitId: toStringOrUndefined(val(dto, "UnitId", "unitId")),
    unitSymbol: val(dto, "UnitSymbol", "unitSymbol"),
    groupKey: val(dto, "GroupKey", "groupKey"),
    operationFamilyIds: Array.isArray(dto.OperationFamilies)
      ? (dto.OperationFamilies.map((f: AnyRecord) =>
          toStringOrUndefined(val(f, "Id", "id"))
        ).filter(Boolean) as string[])
      : undefined,
    isMontageVisible: val(dto, "IsMontageVisible", "isMontageVisible"),
    meta: undefined,
    disabled: val(dto, "Disabled", "disabled"),
    toDeleteOperationFamilyIds: Array.isArray(dto.OperationFamiliesToDelete)
      ? dto.OperationFamiliesToDelete.map((x: any) => String(x))
      : undefined,
  };
}

// ---------------- CoefficientSelectedValue ----------------
export function mapCoefficientSelectedValueDto(
  dto: AnyRecord
): CoefficientSelectedValue {
  return {
    id: toStringOrUndefined(val(dto, "Id", "id")) || "",
    coefficientId: toStringOrUndefined(
      val(dto, "CoefficientId", "coefficientId")
    ),
    optionId: toStringOrUndefined(
      val(
        dto,
        "CoefficientOptionId",
        "coefficientOptionId",
        "OptionId",
        "optionId"
      )
    ),
    value: val(dto, "Value", "value") ?? null,
    secondValue: val(dto, "SecondValue", "secondValue") ?? null,
    flagAction: val(dto, "FlagAction", "flagAction"),
    bopOperationId: toStringOrUndefined(
      val(dto, "BopOperationId", "bopOperationId")
    ),
    siteId: toStringOrUndefined(val(dto, "SiteId", "siteId")),
    groupKey: val(dto, "GroupKey", "groupKey"),
    operationFamilyIds: Array.isArray(dto.OperationFamilyCoefficientOptionList)
      ? (dto.OperationFamilyCoefficientOptionList.map((x: AnyRecord) =>
          toStringOrUndefined(
            val(x, "OperationFamilyId", "operationFamilyId", "Id", "id")
          )
        ).filter(Boolean) as string[])
      : undefined,
    meta: undefined,
  };
}

// ---------------- CoefficientConfig ----------------
export function mapCoefficientConfigDto(dto: AnyRecord): CoefficientConfig {
  return {
    id: toStringOrUndefined(val(dto, "Id", "id")),
    key: val(dto, "Key", "key"),
    configValue: val(dto, "ConfigValue", "configValue"),
    raw: dto,
  };
}

// ---------------- Coefficient ----------------
export function mapCoefficientDto(dto: AnyRecord): Coefficient {
  const optionList = Array.isArray(dto.Options) ? dto.Options : dto.options;
  const selectedValuesList = Array.isArray(dto.SelectedValues)
    ? dto.SelectedValues
    : dto.selectedValues;
  return {
    id: toStringOrUndefined(val(dto, "Id", "id")) || "",
    name: val(dto, "Name", "name") ?? "",
    type: (val(dto, "Type", "type") ?? "Other") as any,
    options: Array.isArray(optionList)
      ? optionList.map(mapCoefficientOptionDto)
      : undefined,
    config: dto.Config ? mapCoefficientConfigDto(dto.Config) : undefined,
    selectedValues: Array.isArray(selectedValuesList)
      ? selectedValuesList.map(mapCoefficientSelectedValueDto)
      : undefined,
    isLocked: val(dto, "IsLocked", "isLocked"),
    language: val(dto, "Language", "language"),
  };
}

// ---------------- BopOperation ----------------
export function mapBopOperationDto(dto: AnyRecord): BopOperation {
  return {
    id: toStringOrUndefined(val(dto, "Id", "id")) || "",
    code: val(dto, "Code", "code") ?? "",
    label: val(dto, "Label", "label", "Name", "name") ?? "",
    unit: val(dto, "Unit", "unit"),
    unitSymbol: val(dto, "UnitSymbol", "unitSymbol"),
    unitId: toStringOrUndefined(val(dto, "UnitId", "unitId")),
    quantity: val(dto, "Quantity", "quantity"),
    function: val(dto, "Function", "function"),
    isCountable: val(dto, "IsCountable", "isCountable"),
    operationUnitValue: val(dto, "OperationUnitValue", "operationUnitValue"),
    coefficients:
      dto.coefficients && typeof dto.coefficients === "object"
        ? Object.keys(dto.coefficients).reduce(
            (acc: Record<string, number>, k) => {
              const num = dto.coefficients[k];
              if (typeof num === "number") acc[k] = num;
              else if (!isNaN(Number(num))) acc[k] = Number(num);
              return acc;
            },
            {}
          )
        : undefined,
    operationFamilyFunctionOperationUnitPoints: val(
      dto,
      "OperationFamilyFunctionOperationUnitPoints",
      "operationFamilyFunctionOperationUnitPoints"
    ),
    operationFamilyFunctionUnitPoints: val(
      dto,
      "OperationFamilyFunctionUnitPoints",
      "operationFamilyFunctionUnitPoints"
    ),
    operationFamilyKey: val(dto, "OperationFamilyKey", "operationFamilyKey"),
    operationFamilyId: toStringOrUndefined(
      val(dto, "OperationFamilyId", "operationFamilyId")
    ),
    operationMesureUnitValue: val(
      dto,
      "OperationMesureUnitValue",
      "operationMesureUnitValue"
    ),
    coefficientIds: Array.isArray(dto.CoefficientIds)
      ? dto.CoefficientIds.map((x: any) => String(x))
      : undefined,
    basePrice: val(dto, "BasePrice", "basePrice"),
    totalPrice: val(dto, "TotalPrice", "totalPrice"),
  };
}

// ---------------- Standalone Operation (Administration listing) ----------------
export function mapOperationDto(dto: AnyRecord): Operation {
  if (!dto) return { id: "" } as Operation;
  const coeffsRaw = dto.Coefficients || dto.coefficients;
  let coefficients: Record<string, number> | undefined;
  if (coeffsRaw && typeof coeffsRaw === "object" && !Array.isArray(coeffsRaw)) {
    coefficients = Object.keys(coeffsRaw).reduce(
      (acc: Record<string, number>, k) => {
        const v = coeffsRaw[k];
        if (typeof v === "number") acc[k] = v;
        else if (!isNaN(Number(v))) acc[k] = Number(v);
        return acc;
      },
      {}
    );
  }
  return {
    id: toStringOrUndefined(val(dto, "Id", "id")) || "",
    code: val(dto, "Code", "code") ?? "",
    name: val(dto, "Name", "name"),
    function: val(dto, "Function", "function"),
    isCountable: val(dto, "IsCountable", "isCountable"),
    unitId: toStringOrUndefined(val(dto, "UnitId", "unitId")),
    unitSymbol: val(dto, "UnitSymbol", "unitSymbol"),
    operationFamilyId: toStringOrUndefined(
      val(dto, "OperationFamilyId", "operationFamilyId")
    ),
    operationFamilyKey: val(dto, "OperationFamilyKey", "operationFamilyKey"),
    operationFamilyName: val(dto, "OperationFamilyName", "operationFamilyName"),
    operationFamilyPath: val(dto, "OperationFamilyPath", "operationFamilyPath"),
    operationType: val(dto, "OperationType", "operationType"),
    coefficients,
    raw: dto,
  };
}

// ---------------- Bop (Lite / Full) ----------------
export function mapBopDto(dto: AnyRecord): Bop {
  if (!dto) return { id: "" } as Bop;
  const opsPrincipal = Array.isArray(dto.OperationPrincipalGroups)
    ? dto.OperationPrincipalGroups
    : dto.OperationPrincipal;
  const opsDiverse = Array.isArray(dto.OperationDiverseGroups)
    ? dto.OperationDiverseGroups
    : dto.OperationDiverse;
  const opsSupp = Array.isArray(dto.OperationSuppGroups)
    ? dto.OperationSuppGroups
    : dto.OperationSupp;
  // Flatten operations if groups have a 'Operations' array
  const operationAggregator: AnyRecord[] = [];
  [opsPrincipal, opsDiverse, opsSupp].forEach((groupList) => {
    if (Array.isArray(groupList)) {
      groupList.forEach((g: AnyRecord) => {
        if (Array.isArray(g.Operations))
          operationAggregator.push(...g.Operations);
      });
    }
  });

  const coefficientsRaw =
    dto.SelectedGlobalCoefficients || dto.selectedGlobalCoefficients;

  const bop: Bop = {
    id: toStringOrUndefined(val(dto, "Id", "id")) || "",
    name: val(dto, "Name", "name"),
    bopNumber: val(dto, "BopNumber", "bopNumber"),
    bopModelKey: val(dto, "BopModelKey", "bopModelKey"),
    bopModelName: val(dto, "BopModelName", "bopModelName"),
    siteId: toStringOrUndefined(val(dto, "SiteId", "siteId")),
    siteName: val(dto, "SiteName", "siteName"),
    entrepriseId: toStringOrUndefined(val(dto, "EntrepriseId", "entrepriseId")),
    bopStatusKey: val(dto, "BopStatusKey", "bopStatusKey"),
    bopStatusName: val(dto, "BopStatusName", "bopStatusName"),
    isSimulation: val(dto, "IsSimulation", "isSimulation"),
    createdDate: normalizeDate(val(dto, "CreatedDate", "createdDate")),
    createdBy: val(dto, "CreatedBy", "createdBy"),
    updatedDate: normalizeDate(val(dto, "UpdatedDate", "updatedDate")),
    updatedBy: val(dto, "UpdatedBy", "updatedBy"),
    totalPoints: val(dto, "TotalPoints", "totalPoints"),
    totalOperationPrincipalPoints: val(
      dto,
      "TotalOperationPrincipalPoints",
      "totalOperationPrincipalPoints"
    ),
    totalOperationDiversePoints: val(
      dto,
      "TotalOperationDiversePoints",
      "totalOperationDiversePoints"
    ),
    totalOperationSuppHeurePoints: val(
      dto,
      "TotalOperationSuppHeurePoints",
      "totalOperationSuppHeurePoints"
    ),
    totalOperationSuppEpiPoints: val(
      dto,
      "TotalOperationSuppEpiPoints",
      "totalOperationSuppEpiPoints"
    ),
    totalSuppHauteurPoints: val(
      dto,
      "TotalSuppHauteurPoints",
      "totalSuppHauteurPoints"
    ),
    pointValue: val(dto, "PointValue", "pointValue"),
    startDate: normalizeDate(val(dto, "StartDate", "startDate")),
    endDate: normalizeDate(val(dto, "EndDate", "endDate")),
    productionDate: normalizeDate(val(dto, "ProductionDate", "productionDate")),
    interventionPlace: val(dto, "InterventionPlace", "interventionPlace"),
    otNumber: val(dto, "OTNumber", "otNumber"),
    daNumber: val(dto, "DANumber", "daNumber"),
    itemDa: val(dto, "ItemDA", "itemDA"),
    wbsElement: val(dto, "WBSElement", "wbsElement"),
    purchaseOrder: val(dto, "PurchaseOrder", "purchaseOrder"),
    itemNumber: val(dto, "ItemNumber", "itemNumber"),
    imputationType: val(dto, "ImputationType", "imputationType"),
    costCenter: val(dto, "CostCenter", "costCenter"),
    operations: operationAggregator.map(mapBopOperationDto),
    coefficients: Array.isArray(dto.Coefficients)
      ? dto.Coefficients.map(mapCoefficientDto)
      : undefined,
    complementaryData: dto.ComplementaryData || dto.complementaryData,
    selectedGlobalCoefficients: coefficientsRaw,
    visas: Array.isArray(dto.Visas) ? dto.Visas : undefined,
    orderGiverUserId: val(dto, "OrderGiverUserId", "orderGiverUserId"),
    orderGiverUserFullName: val(
      dto,
      "OrderGiverUserFullName",
      "orderGiverUserFullName"
    ),
    bopModelId: toStringOrUndefined(val(dto, "BopModelId", "bopModelId")),
    typeTravaux: val(dto, "TypeTravaux", "typeTravaux"),
    isCreatedBySap: val(dto, "IsCreatedBySap", "isCreatedBySap"),
    idSapOrderGiver: val(dto, "IdSapOrderGiver", "idSapOrderGiver"),
    idSapSupplier: val(dto, "IdSapSupplier", "idSapSupplier"),
    idSapSite: val(dto, "IdSapSite", "idSapSite"),
    idFss: val(dto, "IdFss", "idFss"),
    equipement: val(dto, "Equipement", "equipement"),
    sapPrestationServiceNumber: val(
      dto,
      "SapPrestationServiceNumber",
      "sapPrestationServiceNumber"
    ),
    sapSupplementServiceNumber: val(
      dto,
      "SapSupplementServiceNumber",
      "sapSupplementServiceNumber"
    ),
    sapEPIServiceNumber: val(dto, "SapEPIServiceNumber", "sapEPIServiceNumber"),
    initDismantleMailDate: normalizeDate(
      val(dto, "InitDismantleMailDate", "initDismantleMailDate")
    ),
    lastDismantleMailDate: normalizeDate(
      val(dto, "LastDismantleMailDate", "lastDismantleMailDate")
    ),
    lastSignatureDate: normalizeDate(
      val(dto, "LastSignatureDate", "lastSignatureDate")
    ),
    hasFebSignature: val(dto, "HasFebSignature", "hasFebSignature"),
    summaryComment: val(dto, "SummaryComment", "summaryComment"),
    files: Array.isArray(dto.Files) ? dto.Files : dto.files,
    feb: dto.Feb || dto.feb,
    // Nested FEBs for MultiEchafaudage
    febs: Array.isArray(dto.Febs)
      ? dto.Febs.map(mapFebDto)
      : Array.isArray(dto.febs)
      ? dto.febs.map(mapFebDto)
      : undefined,
    heightSupplements: Array.isArray(dto.HeightSupplements) ? dto.HeightSupplements : dto.heightSupplements,
    deletedOperations: Array.isArray(dto.DeletedOperations) ? dto.DeletedOperations : dto.deletedOperations,
    operationPrincipalGroups: Array.isArray(opsPrincipal) ? opsPrincipal : undefined,
    operationDiverseGroups: Array.isArray(opsDiverse) ? opsDiverse : undefined,
    operationSuppGroups: Array.isArray(opsSupp) ? opsSupp : undefined,
    multipleSelectedGlobalCoefficients:
      dto.MultipleSelectedGlobalCoefficients ||
      dto.multipleSelectedGlobalCoefficients,
  };
  return bop;
}

// ---------------- FEB + SubBOP (nested) ----------------
export function mapFebDto(dto: AnyRecord): AnyRecord {
  if (!dto) return {};
  const feb: AnyRecord = {
    id: toStringOrUndefined(val(dto, 'Id', 'id')),
    bopId: toStringOrUndefined(val(dto, 'BopId', 'bopId')),
    accessNumber: val(dto, 'AccessNumber', 'accessNumber'),
    index: val(dto, 'index'),
    unity: val(dto, 'Unity', 'unity'),
    unityState: val(dto, 'UnityState', 'unityState'),
    isPermanent: val(dto, 'IsPermanent', 'isPermanent'),
    workDescription: val(dto, 'WorkDescription', 'workDescription'),
    prescriptions: val(dto, 'Prescriptions', 'prescriptions'),
    repereEquipements: val(dto, 'RepereEquipements', 'repereEquipements'),
    niveauPlancherTravail: val(dto, 'NiveauPlancherTravail', 'niveauPlancherTravail'),
    chargeSpecifiquePlanchers: toStringOrUndefined(val(dto, 'ChargeSpecifiquePlanchers', 'chargeSpecifiquePlanchers')),
    nombrePlancherTravail: toStringOrUndefined(val(dto, 'NombrePlancherTravail', 'nombrePlancherTravail')),
    bachageFiletCoupeVent: val(dto, 'BachageFiletCoupeVent', 'bachageFiletCoupeVent'),
    bachageIgnifuge: val(dto, 'BachageIgnifuge', 'bachageIgnifuge'),
    echafaudageSuspendu: val(dto, 'EchafaudageSuspendu', 'echafaudageSuspendu'),
    echafaudageRoulant: val(dto, 'EchafaudageRoulant', 'echafaudageRoulant'),
    echafaudageInterne: val(dto, 'EchafaudageInterne', 'echafaudageInterne'),
    echafaudageGardeCorps: val(dto, 'EchafaudageGardeCorps', 'echafaudageGardeCorps'),
    echafaudageTypePasserelle: val(dto, 'EchafaudageTypePasserelle', 'echafaudageTypePasserelle'),
    dismantleDoneDate: normalizeDate(
      val(dto, "DismantleDoneDate", "dismantleDoneDate")
    ),
    // SubBOPs nested
    subBops: Array.isArray(dto.SubBops)
      ? dto.SubBops.map(mapSubBopDto)
      : Array.isArray(dto.subBops)
      ? dto.subBops.map(mapSubBopDto)
      : undefined,
    visas: Array.isArray(dto.visas) ? dto.visas : undefined,
  };
  // Remove undefineds
  Object.keys(feb).forEach(k => { if (feb[k] === undefined) delete feb[k]; });
  return feb;
}

export function mapSubBopDto(dto: AnyRecord): AnyRecord {
  if (!dto) return {};
  const sub: AnyRecord = {
    id: toStringOrUndefined(val(dto, 'Id', 'id')),
    febId: toStringOrUndefined(val(dto, 'FebId', 'febId')),
    name: val(dto, 'Name', 'name'),
    sequenceOrder: val(dto, 'SequenceOrder', 'sequenceOrder'),
    totalPoints: val(dto, 'TotalPoints', 'totalPoints'),
    totalOperationPrincipalPoints: val(dto, 'TotalOperationPrincipalPoints', 'totalOperationPrincipalPoints'),
    totalOperationDiversePoints: val(dto, 'TotalOperationDiversePoints', 'totalOperationDiversePoints'),
    totalOperationSuppHeurePoints: val(dto, 'TotalOperationSuppHeurePoints', 'totalOperationSuppHeurePoints'),
    totalOperationSuppEpiPoints: val(dto, 'TotalOperationSuppEpiPoints', 'totalOperationSuppEpiPoints'),
    totalSuppHauteurPoints: val(dto, 'TotalSuppHauteurPoints', 'totalSuppHauteurPoints'),
    // Operation groups under SubBOP
    operationPrincipalGroups: Array.isArray(dto.OperationPrincipalGroups) ? dto.OperationPrincipalGroups : undefined,
    operationDiverseGroups: Array.isArray(dto.OperationDiverseGroups) ? dto.OperationDiverseGroups : undefined,
    operationSuppGroups: Array.isArray(dto.OperationSuppGroups) ? dto.OperationSuppGroups : undefined,
    multipleSelectedGlobalCoefficients: dto.multipleSelectedGlobalCoefficients,
    isValid: true,
  };
  Object.keys(sub).forEach(k => { if (sub[k] === undefined) delete sub[k]; });
  return sub;
}

// ---------------- Reverse minimal mappings ----------------
export function toBopDto(domain: Bop): AnyRecord {
  const dto: AnyRecord = {
    Id: domain.id,
    Name: domain.name,
    BopModelKey: domain.bopModelKey,
    BopModelName: domain.bopModelName,
    SiteId: domain.siteId,
    EntrepriseId: domain.entrepriseId,
    BopStatusKey: domain.bopStatusKey,
    IsSimulation: domain.isSimulation,
    StartDate: domain.startDate,
    EndDate: domain.endDate,
    OrderGiverUserId: domain.orderGiverUserId,
    ProductionDate: domain.productionDate,
    Operations:
      domain.operations?.map((o) => ({
        Id: o.id,
        Code: o.code,
        Label: o.label,
        Unit: o.unit ?? o.unitSymbol,
        UnitSymbol: o.unitSymbol,
        UnitId: o.unitId,
        Quantity: o.quantity,
      })) || [],
    // SelectedGlobalCoefficients left as-is for backend compatibility
    SelectedGlobalCoefficients: domain.selectedGlobalCoefficients,
    MultipleSelectedGlobalCoefficients:
      domain.multipleSelectedGlobalCoefficients,
    BopModelId: domain.bopModelId,
    TypeTravaux: domain.typeTravaux,
    IsCreatedBySap: domain.isCreatedBySap,
    IdSapOrderGiver: domain.idSapOrderGiver,
    IdSapSupplier: domain.idSapSupplier,
    IdSapSite: domain.idSapSite,
    IdFss: domain.idFss,
    Equipement: domain.equipement,
    SapPrestationServiceNumber: domain.sapPrestationServiceNumber,
    SapSupplementServiceNumber: domain.sapSupplementServiceNumber,
    SapEPIServiceNumber: domain.sapEPIServiceNumber,
    InitDismantleMailDate: domain.initDismantleMailDate,
    LastDismantleMailDate: domain.lastDismantleMailDate,
    LastSignatureDate: domain.lastSignatureDate,
    HasFebSignature: domain.hasFebSignature,
    SummaryComment: domain.summaryComment,
    DeletedOperations: domain.deletedOperations,
  };
  // Prune undefined to avoid sending noisy keys
  Object.keys(dto).forEach((k) => {
    if (dto[k] === undefined) delete dto[k];
  });
  return dto;
}

export function toCoefficientSelectedValueDto(
  domain: CoefficientSelectedValue
): AnyRecord {
  return {
    Id: domain.id,
    CoefficientId: domain.coefficientId,
    CoefficientOptionId: domain.optionId,
    Value: domain.value,
    SecondValue: domain.secondValue,
    FlagAction: domain.flagAction,
  };
}

export function toCoefficientOptionDto(domain: CoefficientOption): AnyRecord {
  return {
    Id: domain.id,
    Key: domain.key,
    Name: domain.name || domain.label,
    Value: domain.value,
    SecondValue: domain.secondValue,
    FlagAction: domain.flagAction,
    UnitId: domain.unitId,
    UnitSymbol: domain.unitSymbol,
    IsMontageVisible: domain.isMontageVisible,
  };
}

// Utility batch mapping
export function mapBopList(dtos: AnyRecord[]): Bop[] {
  return dtos.map(mapBopDto);
}
